import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import './Header.scss';
import BreadCrumb from '../../common/components/Breadcrumb/Breadcrumb';
import FarmLocalTimezone from '../FarmLocalTimezone/FarmLocalTimezone';
import SettingsTabs from '../../containers/SettingsPage/SettingsTabs';
import EntityStatus from '../EntityState/EntityState';
import { useAppSelector } from '../../reducers/rootReducer';
import FarmFavouriteActions from '@/containers/FarmsPage/FarmFavouriteActions';
import useFarms from '../App/hooks/useFarms';
import { useLelyAuth } from '../../auth/useLelyAuth';
import {
  useGetFarmDetailsQuery,
  useGetFarmStateQuery,
} from '../../rtk/farmDetailsApi';
import { userDetailsSelectors } from '../../reducers/userDetailsSlice';
import RingBuffer from '../RingBuffer/RingBuffer';
import { featureFlagsSelectors } from '@/reducers/featureFlagSlice';
import { MIN_SUPPORTED_SCREEN_WIDTH, RING_BUFFER_FF } from '@/common/constants';
import { useMediaQuery } from '@mui/material';
import { useFarmIdParam, usePageMatch, useUrlEntityId } from '../App/hooks';
import { useGetEntityInfoQuery } from '@/rtk/entityInfoApi';

const Header = () => {
  const navigate = useNavigate();

  const { isAuthenticated, user } = useLelyAuth();

  const farmId = useFarmIdParam();
  const entityId = useUrlEntityId();

  const {
    isFarmDetailsPage,
    isVDEPage,
    isEntityPage,
    isSettingsPage,
    isRingBufferPage,
  } = usePageMatch();

  const preferredSystems = useAppSelector(
    userDetailsSelectors.selectPreferredSystems,
  );
  const isRingBufferAllowed = useAppSelector((state) =>
    featureFlagsSelectors.isFlagActive(state, RING_BUFFER_FF),
  );

  const isUnderMinSupportedWidth = useMediaQuery(
    `(max-width:${MIN_SUPPORTED_SCREEN_WIDTH}px)`,
  );

  const { data: entityInfo } = useGetEntityInfoQuery(entityId, {
    skip: !entityId,
  });

  const { data: farmDetails } = useGetFarmDetailsQuery(
    {
      farmId: farmId || '',
      preferredSystems,
    },
    {
      skip: !isFarmDetailsPage || !farmId || !preferredSystems?.length,
    },
  );

  const { data: farmState } = useGetFarmStateQuery(entityInfo?.farmId || '', {
    skip: !isRingBufferPage || !entityInfo?.farmId,
  });
  const { farmRingBufferEnabled } = farmState || {};

  const { favouriteFarms } = useFarms();

  const isRingBufferActive =
    isRingBufferPage && isRingBufferAllowed && farmRingBufferEnabled;

  const hideBreadcrumb = isVDEPage && isUnderMinSupportedWidth; // Related to min supported width calculations on VDE

  const isFavourite = !!favouriteFarms.find((f) => f.id === farmDetails?.id);

  let breadcrumbComponent: ReactElement<any, any>;
  let favouriteFarmButton: ReactElement<any, any> | null = null;
  let entityStatusAndLocalTimezoneComponents: ReactElement[] = [];

  if (isFarmDetailsPage && farmDetails) {
    breadcrumbComponent = (
      <BreadCrumb farmDetails={farmDetails} entityInfo={entityInfo} />
    );
    entityStatusAndLocalTimezoneComponents = [
      <FarmLocalTimezone key="farmlcl" farmDetails={farmDetails} />,
    ];
    favouriteFarmButton = (
      <FarmFavouriteActions
        farm={{
          id: farmDetails.id,
          isFavourite,
        }}
      />
    );
  } else if (isEntityPage) {
    entityStatusAndLocalTimezoneComponents = [
      <EntityStatus key="entityst" />,
      <FarmLocalTimezone key="farmlcl" farmDetails={farmDetails} />,
      ...(isRingBufferActive ? [<RingBuffer key="ringbuffer" />] : []),
    ];
    breadcrumbComponent = (
      <div className="settings-breadcrumb-container">
        <BreadCrumb farmDetails={farmDetails} entityInfo={entityInfo} />
        {isSettingsPage && <SettingsTabs />}
      </div>
    );
  } else {
    breadcrumbComponent = (
      <div className="settings-breadcrumb-container">
        <BreadCrumb farmDetails={farmDetails} entityInfo={entityInfo} />
        {isSettingsPage && <SettingsTabs />}
      </div>
    );
  }

  return isAuthenticated ? (
    <div className="top-navigation-fixed-wrapper" id="app-header">
      <div className="top-navigation" data-testid="div-header">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            if (user?.allowltda) {
              navigate('/farms');
            }
          }}
          className="top-navigation__app-title"
          translate="no"
        >
          <h1>Lely Technician Diagnostic Assistant</h1>
        </a>

        {!hideBreadcrumb && (
          <>
            {breadcrumbComponent}
            <div className="top-navigation__inner">
              <div className="top-navigation__btns d-none d-sm-flex">
                {entityStatusAndLocalTimezoneComponents}
                {favouriteFarmButton}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  ) : null;
};
export default Header;
